import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Space, Typography } from 'antd';
import {
  BookOutlined,
  NotificationOutlined,
  CommentOutlined,
  BugOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  BellOutlined,
  SettingOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  LoginOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../common/LanguageSwitcher';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // 화면 크기 감지
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    // 모바일에서 메뉴 클릭 시 사이드바 자동 숨김
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLogin = () => {
    navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('navigation.profile'),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      onClick: handleLogout,
    },
  ];

  const menuItems: any[] = [
    {
      key: '/laws',
      icon: <BookOutlined />,
      label: t('navigation.laws'),
    },
    {
      key: '/publications',
      icon: <FileTextOutlined />,
      label: t('navigation.publications'),
    },
    {
      key: '/legislative-notices',
      icon: <BellOutlined />,
      label: t('navigation.legislativeNotices'),
    },
    {
      key: '/announcements',
      icon: <NotificationOutlined />,
      label: t('navigation.announcements'),
    },
  ];

  // 인증된 사용자에게만 보이는 메뉴들
  if (isAuthenticated) {
    menuItems.push(
      {
        key: '/comments',
        icon: <CommentOutlined />,
        label: t('navigation.comments'),
      },
      {
        key: '/bug-reports',
        icon: <BugOutlined />,
        label: t('navigation.bugReports'),
      }
    );
  }

  // 사용법 메뉴 (모든 사용자에게 표시)
  menuItems.push({
    key: '/manuals',
    icon: <QuestionCircleOutlined />,
    label: t('navigation.manuals'),
  });

  // 관리자에게만 보이는 메뉴들
  if (isAdmin) {
    menuItems.push(
      {
        key: '/admin',
        icon: <SettingOutlined />,
        label: t('navigation.admin'),
        children: [
          {
            key: '/admin/users',
            icon: <TeamOutlined />,
            label: t('navigation.userManagement'),
          },
          {
            key: '/admin/email',
            icon: <MailOutlined />,
            label: t('navigation.emailSender'),
          },
          {
            key: '/admin/email-logs',
            icon: <MailOutlined />,
            label: t('navigation.emailLogManagement'),
          },
        ],
      }
    );
  }

  return (
    <Layout>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        collapsedWidth={isMobile ? 0 : 80}
        width={isMobile ? 200 : 200}
        style={{
          position: isMobile ? 'fixed' : 'relative',
          height: '100vh',
          zIndex: isMobile ? 1000 : 1,
          left: isMobile && !collapsed ? 0 : undefined,
        }}
      >
        <div 
          style={{ 
            height: 64, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'white',
            fontSize: collapsed ? 16 : 18,
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
          onClick={() => navigate('/')}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {collapsed ? t('navigation.shortTitle') : t('navigation.title')}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      
      {/* 모바일에서 사이드바 오버레이 */}
      {isMobile && !collapsed && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
          onClick={() => setCollapsed(true)}
        />
      )}
      
      <Layout>
        <Header style={{ 
          padding: isMobile ? '0 8px' : '0 16px', 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          height: isMobile ? 56 : 64,
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ 
              fontSize: isMobile ? '14px' : '16px', 
              width: isMobile ? 48 : 64, 
              height: isMobile ? 48 : 64 
            }}
          />
          
          <Space>
            <LanguageSwitcher />
            {isAuthenticated ? (
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} />
                  {!isMobile && (
                    <div style={{ lineHeight: '1.2' }}>
                      <Text strong>{user?.username}</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {user?.role === 'ADMIN' ? t('users.admin') : t('users.general')}
                        </Text>
                      </div>
                    </div>
                  )}
                </Space>
              </Dropdown>
            ) : (
              <Button 
                type="primary" 
                icon={<LoginOutlined />}
                onClick={handleLogin}
                size={isMobile ? 'small' : 'middle'}
              >
                {!isMobile && '로그인'}
              </Button>
            )}
          </Space>
        </Header>
        <Content style={{ 
          margin: isMobile ? '8px' : '16px 12px',
          padding: isMobile ? 16 : 20,
          background: '#fff',
          borderRadius: 8,
          minHeight: 280,
          overflow: 'auto'
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout; 